import { useState } from 'react';
import { Button, TooltipProvider } from '@design-system';
import { fundingPolicyTypeDefs } from '@shared';
import { UnverifiedModelingDisclaimer } from '@client/components/UnverifiedModelingDisclaimer';
import { useProjectionForPolicy, usePolicyById } from '../hooks/useProjectionForPolicy';
import { useIsWizardSession } from '../hooks/useIsWizardSession';
import { PolicyDetailsPopover } from './PolicyDetailsPopover';
import { CarePlanDetailsPopover } from './carePlanDetails/CarePlanDetailsPopover';
import { ReturnsSummaryCard } from './ReturnsSummaryCard';
import { MonthlyBreakdownTable } from './MonthlyBreakdownTable';
import { AccumulationTimelineSection } from './accumulationTimeline/AccumulationTimelineSection';
import { PolicyCostSection } from './policyCost/PolicyCostSection';
import { IdentityChecksPanel } from './IdentityChecksPanel';
import { ProjectionDataWarnings } from './ProjectionDataWarnings';
import { TranscriptDialog } from './TranscriptDialog';
import { EmptyState } from './EmptyState';

interface ProjectionDetailsContentProps {
  fundingSourceId: string | undefined;
  showEngineTools?: boolean;
}

/**
 * The body of the projection-details view: the wizard tools row plus the
 * policy/returns/monthly/identity cards. Shared by `ProjectionDetailsPage`
 * (full page, with a policy picker) and the funding-table slide-out.
 */
export function ProjectionDetailsContent({
  fundingSourceId,
  showEngineTools = true,
}: ProjectionDetailsContentProps) {
  const projection = useProjectionForPolicy(fundingSourceId);
  const policy = usePolicyById(fundingSourceId);
  const isWizard = useIsWizardSession();
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {policy?.policyType &&
          fundingPolicyTypeDefs[policy.policyType]?.hasUnverifiedModeling && (
            <UnverifiedModelingDisclaimer policyType={policy.policyType} />
          )}

        {projection && <ProjectionDataWarnings terms={projection.terms} />}

        {projection && (
          <div className="pointer-events-none sticky top-3 z-30 flex flex-wrap justify-end gap-2 [&_button:hover]:bg-gray-50 [&_button]:pointer-events-auto [&_button]:bg-white [&_button]:shadow-md">
            <PolicyDetailsPopover policy={policy} />
            <CarePlanDetailsPopover />
            {showEngineTools && isWizard && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTranscriptOpen(true)}
              >
                View as text
              </Button>
            )}
          </div>
        )}

        {projection && (
          <TranscriptDialog
            projection={projection}
            open={transcriptOpen}
            onClose={() => setTranscriptOpen(false)}
          />
        )}

        {!projection ? (
          <EmptyState
            title="No projection data yet"
            body="Run a calculation on this client to populate the projection details."
          />
        ) : (
          <div className="space-y-3">
            <AccumulationTimelineSection
              projection={projection}
              policyType={policy?.policyType}
            />
            <MonthlyBreakdownTable
              projection={projection}
              policyType={policy?.policyType}
            />
            <ReturnsSummaryCard projection={projection} />
            <PolicyCostSection policy={policy} terms={projection.terms} />
            <IdentityChecksPanel projection={projection} />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
